// pages/Orders/OrderDetailsPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import { orderApiService } from "services/orderApiService";

const OrderDetailsPage = () => {
  const { id } = useParams();
  console.log("id", id);
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [medicineSearch, setMedicineSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAddingMedicine, setIsAddingMedicine] = useState(false);

  // Fetch order and discount data
  const fetchOrderData = useCallback(async () => {
    setLoading(true);
    try {
      const [orderData, discountData] = await Promise.all([
        orderApiService.getOrder(id),
        orderApiService.getMedicineDiscount()
      ]);

      setOrder(orderData);
      setGlobalDiscount(parseFloat(discountData.medicinediscount) || 0);
    } catch (error) {
      toast.error("Failed to load order details");
      console.error("Error fetching order data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  // Handle medicine search
  const handleMedicineSearch = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await orderApiService.searchMedicines(query);
      setSearchResults(results);
    } catch (error) {
      console.error("Error searching medicines:", error);
    }
  }, []);

  // Calculate product prices based on discount
  const calculateProductPrices = (medicine, qty = 1) => {
    const regularPrice = parseFloat(medicine.mrp || medicine.regularunitprice || 0);
    const discountDecimal = globalDiscount / 100;
    const discountPrice = regularPrice - (regularPrice * discountDecimal);

    return {
      productid: medicine.id,
      image: medicine.imagelink || "https://static.vecteezy.com/system/resources/thumbnails/002/272/156/small_2x/medicine-drug-icon-flat-style-and-colorful-design-illustration-free-vector.jpg",
      tytle: medicine.productname || medicine.medicinename,
      type: medicine.category === 'Baby' ? 'healthmart' : 'medicine',
      power: medicine.power || medicine.size || 'none',
      ptype: medicine.ptype || medicine.packaging || 'none',
      quantity: qty,
      regularPrice: regularPrice,
      discountPrice: parseFloat(discountPrice.toFixed(2)),
      totalprice: parseFloat((discountPrice * qty).toFixed(2)),
      totalregularPrice: parseFloat((regularPrice * qty).toFixed(2))
    };
  };

  // Add medicine to order
  const handleAddMedicine = () => {
    if (!selectedMedicine || quantity < 1) {
      toast.error("Please select a medicine and quantity");
      return;
    }

    const newProduct = calculateProductPrices(selectedMedicine, quantity);

    setOrder(prev => {
      const updatedProducts = [...(prev.orderproductdata || []), newProduct];
      return {
        ...prev,
        orderproductdata: updatedProducts
      };
    });

    // Recalculate totals
    setTimeout(() => {
      recalculateOrderTotals();
    }, 100);

    toast.success(`${selectedMedicine.productname || selectedMedicine.medicinename} added to order`);
    setSelectedMedicine(null);
    setQuantity(1);
    setMedicineSearch("");
    setSearchResults([]);
    setIsAddingMedicine(false);
  };

  // Remove medicine from order
  const handleRemoveMedicine = (index) => {
    setOrder(prev => {
      const updatedProducts = prev.orderproductdata.filter((_, i) => i !== index);
      return {
        ...prev,
        orderproductdata: updatedProducts
      };
    });

    // Recalculate totals
    setTimeout(() => {
      recalculateOrderTotals();
    }, 100);

    toast.success("Medicine removed from order");
  };

  // Update medicine quantity
  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;

    setOrder(prev => {
      const updatedProducts = [...prev.orderproductdata];
      const product = updatedProducts[index];

      product.quantity = newQuantity;
      product.totalprice = parseFloat((product.discountPrice * newQuantity).toFixed(2));
      product.totalregularPrice = parseFloat((product.regularPrice * newQuantity).toFixed(2));

      return {
        ...prev,
        orderproductdata: updatedProducts
      };
    });

    // Recalculate totals
    setTimeout(() => {
      recalculateOrderTotals();
    }, 100);
  };

  // Recalculate order totals
  const recalculateOrderTotals = () => {
    setOrder(prev => {
      const products = prev.orderproductdata || [];

      const totalquantity = products.reduce((sum, product) => sum + (parseInt(product.quantity) || 0), 0);
      const totalproductamount = products.reduce((sum, product) => sum + (parseFloat(product.totalprice) || 0), 0);
      const totalMRP = products.reduce((sum, product) => sum + (parseFloat(product.totalregularPrice) || 0), 0);

      const deliverycharge = parseFloat(prev.deliverycharge) || 0;
      const totalamount = totalproductamount + deliverycharge;

      return {
        ...prev,
        totalquantity: totalquantity.toString(),
        totalproductamount: totalproductamount.toFixed(2),
        totalMRP: totalMRP.toFixed(2),
        totalamount: totalamount.toFixed(2),
        suppertotalamount: totalamount.toFixed(2)
      };
    });
  };

  // Save order updates
  const handleSaveOrder = async () => {
    if (!order) return;

    setSaving(true);
    try {
      // Prepare update data
      const updateData = {
        orderproductdata: order.orderproductdata,
        totalquantity: order.totalquantity,
        totalproductamount: order.totalproductamount,
        totalMRP: order.totalMRP,
        totalamount: order.totalamount,
        suppertotalamount: order.suppertotalamount,
        username: order.username,
        phonenumber: order.phonenumber,
        address: order.address,
        notes: order.notes || "",
        orderstatus: order.orderstatus,
        paymentstatus: order.paymentstatus,
        deliverytype: order.deliverytype,
        deliverycharge: order.deliverycharge
      };

      await orderApiService.updateOrder(id, updateData);
      toast.success("Order updated successfully");

      // Refresh data
      fetchOrderData();
    } catch (error) {
      toast.error("Failed to update order");
      console.error("Error updating order:", error);
    } finally {
      setSaving(false);
    }
  };

  // Delete order
  const handleDeleteOrder = async () => {
    setDeleting(true);
    try {
      await orderApiService.deleteOrder(id);
      toast.success("Order deleted successfully");
      navigate("/all-orders");
    } catch (error) {
      toast.error("Failed to delete order");
      console.error("Error deleting order:", error);
    } finally {
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD', { minimumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString;
  };

  if (loading) {
    return <DashboardLoader />;
  }

  if (!order) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="Order Details" breadcrumbItem="Order Not Found" />
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Order not found. It may have been deleted or doesn't exist.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb
          title={`Order #${order.id}`}
          breadcrumbItem="Order Details"
        />

        {/* Header Actions */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <button
              className="btn btn-light me-2"
              onClick={() => navigate("/all-orders")}
            >
              <i className="fas fa-arrow-left me-2"></i> Back to Orders
            </button>
            {/* <span className={`badge fs-6 ${order.orderstatus === 'completed' ? 'bg-success' : order.orderstatus === 'cancelled' ? 'bg-danger' : 'bg-warning'}`}>
              {order.orderstatus?.toUpperCase()}
            </span> */}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-danger"
              onClick={() => setDeleteModal(true)}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash me-2"></i> Delete Order
                </>
              )}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveOrder}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i> Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        <div className="row">
          {/* Left Column - Customer Information & Order Summary */}
          <div className="col-lg-4">
            {/* Customer Information Card */}
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-user me-2"></i>Customer Information
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Customer Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={order.username || ""}
                    onChange={(e) => setOrder({ ...order, username: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={order.phonenumber || ""}
                    onChange={(e) => setOrder({ ...order, phonenumber: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={order.address || ""}
                    onChange={(e) => setOrder({ ...order, address: e.target.value })}
                  />
                </div>
                {/* <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={order.notes || ""}
                    onChange={(e) => setOrder({ ...order, notes: e.target.value })}
                    placeholder="Add order notes..."
                  />
                </div> */}
              </div>
            </div>

            {/* Order Summary Card */}
            <div className="card mt-3">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-receipt me-2"></i>Order Summary
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Order Status</label>
                  <select
                    className="form-select"
                    value={order.orderstatus || "submitted"}
                    onChange={(e) => setOrder({ ...order, orderstatus: e.target.value })}
                  >
                    <option value="submitted">Submitted</option>
                    <option value="processing">Processing</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Delivered">Delivered</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select"
                    value={order.paymentstatus || "unpaid"}
                    onChange={(e) => setOrder({ ...order, paymentstatus: e.target.value })}
                  >
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Delivery Type</label>
                  <select
                    className="form-select"
                    value={order.deliverytype || "Regular"}
                    onChange={(e) => setOrder({ ...order, deliverytype: e.target.value })}
                  >
                    <option value="Regular">Regular</option>
                    <option value="Express">Express</option>
                  </select>
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">Delivery Charge (৳)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={order.deliverycharge || "0"}
                    onChange={(e) => setOrder({ ...order, deliverycharge: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div> */}

                <hr />

                <div className="mb-2 d-flex justify-content-between">
                  <span>Order Date:</span>
                  <strong>{formatDate(order.orderdate)}
                    {/* {order.ordertime} */}
                  </strong>
                </div>

                <div className="mb-2 d-flex justify-content-between">
                  <span>Payment Method:</span>
                  <strong className={`badge ${order.paymentmethod === 'Cash_on_delivery' ? 'bg-success' : 'bg-primary'}`} style={{ paddingTop: "6px" }}>
                    {order.paymentmethod === 'Cash_on_delivery' ? 'Cash on Delivery' : order.paymentmethod}
                  </strong>
                </div>

                <div className="mb-2 d-flex justify-content-between">
                  <span>Inside Dhaka:</span>
                  <strong>
                    {order.insidedhaka ? (
                      <span className="badge bg-success">Yes</span>
                    ) : (
                      <span className="badge bg-secondary">No</span>
                    )}
                  </strong>
                </div>
              </div>
            </div>

            {/* Order Totals Card */}

          </div>

          {/* Right Column - Medicines Management */}
          <div className="col-lg-8">
            {/* Add Medicine Card */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-plus-circle me-2"></i>Add Medicine to Order
                </h5>
              </div>
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Search Medicine</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name, brand, or generic name..."
                          value={medicineSearch}
                          onChange={(e) => {
                            const value = e.target.value;
                            setMedicineSearch(value);
                            handleMedicineSearch(value);
                          }}
                        />
                        <button className="btn btn-outline-secondary">
                          <i className="fas fa-search"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        value={quantity}
                        onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                        min="1"
                        disabled={!selectedMedicine}
                      />
                    </div>
                  </div> */}

                  <div className="col-md-2 d-flex align-items-end" style={{ marginTop: "10px" }}>
                    <button
                      className="btn btn-success w-100"
                      onClick={handleAddMedicine}
                      disabled={!selectedMedicine}
                    >
                      <i className="fas fa-plus me-2"></i> Add
                    </button>
                  </div>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-3 border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <div className="list-group">
                      {searchResults.map((medicine) => (
                        <button
                          key={medicine.id}
                          type="button"
                          className={`list-group-item list-group-item-action ${selectedMedicine?.id === medicine.id ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedMedicine(medicine);
                            setQuantity(1);
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {medicine.imagelink && (
                              <img
                                src={medicine.imagelink}
                                alt={medicine.productname || medicine.medicinename}
                                className="rounded me-3"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                            )}
                            <div className="text-start">
                              <div className="fw-semibold">
                                {medicine.productname || medicine.medicinename}
                              </div>
                              <small className={selectedMedicine?.id === medicine.id ? 'text-light' : 'text-muted'}>
                                {medicine.productbrand || medicine.companyname} •
                                {medicine.genericname && ` • ${medicine.genericname}`} •
                                MRP: {formatCurrency(medicine.mrp || medicine.regularunitprice)}
                              </small>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Medicine Preview */}
                {/* {selectedMedicine && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Selected:</strong> {selectedMedicine.productname || selectedMedicine.medicinename}
                        <div className="text-muted small">
                          MRP: {formatCurrency(selectedMedicine.mrp || selectedMedicine.regularunitprice)} •
                          After {globalDiscount}% Discount: {formatCurrency(
                            (parseFloat(selectedMedicine.mrp || selectedMedicine.regularunitprice || 0) *
                              (1 - globalDiscount / 100)).toFixed(2)
                          )}
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">
                          Total for {quantity} item{quantity > 1 ? 's' : ''}: {formatCurrency(
                            (parseFloat(selectedMedicine.mrp || selectedMedicine.regularunitprice || 0) *
                              (1 - globalDiscount / 100) * quantity).toFixed(2)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            </div>

            {/* Current Medicines Card */}
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-pills me-2"></i>Ordered Medicines
                    {/* <span className="badge bg-primary ms-2">
                      {order.orderproductdata?.length || 0} items
                    </span> */}
                  </h5>
                  <div className="text-muted">
                    Global Discount: <strong className="text-danger">{globalDiscount}%</strong>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {!order.orderproductdata || order.orderproductdata.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                    <h4>No medicines in this order</h4>
                    <p className="text-muted">Add medicines using the search above</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Medicine</th>
                          <th>Type</th>
                          <th className="text-center">Quantity</th>
                          <th className="text-end">Unit Price</th>
                          <th className="text-end">Discount Price</th>
                          <th className="text-end">Total</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.orderproductdata.map((product, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={product.image}
                                  alt={product.tytle}
                                  className="rounded me-3"
                                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                                <div>
                                  <div className="fw-semibold">{product.tytle}</div>
                                  <small className="text-muted">
                                    {product.ptype !== "none" && `${product.ptype} • `}
                                    {product.power !== "none" && product.power}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${product.type === 'medicine' ? 'bg-danger' : 'bg-info'}`}>
                                {product.type}
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="input-group input-group-sm" style={{ width: '120px', margin: '0 auto' }}>
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={() => handleUpdateQuantity(index, product.quantity - 1)}
                                  disabled={product.quantity <= 1}
                                >
                                  <i className="fas fa-minus"></i>
                                </button>
                                <input
                                  type="text"
                                  className="form-control text-center"
                                  value={product.quantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val)) {
                                      handleUpdateQuantity(index, val);
                                    }
                                  }}
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={() => handleUpdateQuantity(index, product.quantity + 1)}
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                              </div>
                            </td>
                            <td className="text-end">
                              <div className="text-muted text-decoration-line-through">
                                {formatCurrency(product.regularPrice)}
                              </div>
                            </td>
                            <td className="text-end">
                              <div className="fw-bold text-success">
                                {formatCurrency(product.discountPrice)}
                              </div>
                              <small className="text-muted">
                                {globalDiscount}% off
                              </small>
                            </td>
                            <td className="text-end fw-bold">
                              {formatCurrency(product.totalprice)}
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveMedicine(index)}
                                title="Remove from order"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {/* <tfoot>
                        <tr className="table-primary">
                          <td colSpan="3" className="text-end fw-bold">Totals:</td>
                          <td className="text-center fw-bold">{order.totalquantity}</td>
                          <td className="text-end fw-bold text-decoration-line-through">
                            {formatCurrency(order.totalMRP)}
                          </td>
                          <td className="text-end fw-bold text-success">
                            {formatCurrency(order.totalproductamount)}
                          </td>
                          <td className="text-end fw-bold">
                            {formatCurrency(order.totalamount)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot> */}
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Order Notes and Actions */}
            {/* <div className="card mt-4">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Admin Notes</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Add internal notes about this order..."
                        value={order.notes || ""}
                        onChange={(e) => setOrder({ ...order, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      className="btn btn-primary w-100"
                      onClick={handleSaveOrder}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Update Order to Database
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div> */}

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-calculator me-2"></i>Order Totals
                </h5>
              </div>
              <div className="card-body">
                {/* <div className="d-flex justify-content-between mb-2">
                  <span>Total Items:</span>
                  <strong>{order.totalquantity}</strong>
                </div> */}
                <div className="d-flex justify-content-between mb-2">
                  <strong>Subtotal:</strong>
                  <strong>{formatCurrency(order.totalproductamount)}</strong>
                </div>
                {/* <div className="d-flex justify-content-between mb-2">
                  <span>Total MRP Value:</span>
                  <strong className="text-muted text-decoration-line-through">
                    {formatCurrency(order.totalMRP)}
                  </strong>
                </div> */}
                <div className="d-flex justify-content-between mb-2">
                  <strong>Delivery Charge:</strong>
                  <strong>{formatCurrency(order.deliverycharge)}</strong>
                </div>
                {order.promoAmount && parseFloat(order.promoAmount) > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-danger">
                    <strong>Promo Discount:</strong>
                    <strong>-{formatCurrency(order.promoAmount)}</strong>
                  </div>
                )}
                <hr />
                <div className="d-flex justify-content-between">
                  <strong className="h5">Total:</strong>
                  <span className="h4 text-primary">{formatCurrency(order.totalamount)}</span>
                </div>




                {/* <div className="mt-3">
                  <div className="d-flex justify-content-between">
                    <span>Paid Amount:</span>
                    <strong className="text-success">{formatCurrency(order.paidamount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Due Amount:</span>
                    <strong className="text-danger">{formatCurrency(order.dueamount)}</strong>
                  </div>
                </div> */}
              </div>
            </div>

            <div className="card mt-3">

              <div className="card-body d-flex justify-content-end align-items-end">


                <div className="col-md-4 d-flex justify-content-end align-items-end">
                  <button
                    className="btn btn-primary w-100 py-2"
                    onClick={handleSaveOrder}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Update Order to Database
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        loader={deleting}
        onCloseClick={() => setDeleteModal(false)}
        title="Delete Order"
        message="Are you sure you want to delete this order? This action cannot be undone."
      />
    </div>
  );
};

export default OrderDetailsPage;